import { useMemo, useState } from 'react';
import { Collapse, CollapseProps } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useStore } from 'zustand';
import { timesheetsStore } from 'entities/timesheet';
import { getMonthsInRange } from '../../lib/getMonthsInRange';
import { getWorkHoursInRange } from '../../lib/getWorkHoursInRange';
import { CustomColumn, TimesheetTableRow } from '../../TimesheetsTable.types';
import { MonthPanelContent } from '../MonthPanelContent';

import styles from './TimesheetCollapse.module.scss';

type TimesheetCollapseProps = {
  columnsVisibilityMap: Record<string, boolean>;
  allColumns: CustomColumn<TimesheetTableRow>[];
};

const currentMonthKey = dayjs().format('YYYY-MM');

const TimesheetCollapse = ({ columnsVisibilityMap, allColumns }: TimesheetCollapseProps): React.ReactElement => {
  const { t } = useTranslation('Timesheet');

  const [searchParams] = useSearchParams();

  const accountId = searchParams.get('accountId');
  const projectId = searchParams.get('projectId');
  const shouldExpand = !!(accountId || projectId);

  const range = useStore(timesheetsStore, (state) => state.selectedRange);

  const monthsInRange = useMemo(() => getMonthsInRange(range[0], range[1]), [range]);

  const [activeKey, setActiveKey] = useState<string | string[]>(currentMonthKey);

  const items: CollapseProps['items'] = useMemo(
    () =>
      monthsInRange.map((monthItem) => {
        const isActive = Array.isArray(activeKey) ? activeKey.includes(monthItem.key) : activeKey === monthItem.key;

        return {
          key: monthItem.key,
          label: (
            <div>
              {t(monthItem.date.format('MMMM'))} {monthItem.date.format('YYYY')}
              {isActive && (
                <span className={styles.badge}>
                  {getWorkHoursInRange(
                    monthItem.date.startOf('month').format('YYYY-MM-DD'),
                    monthItem.date.endOf('month').format('YYYY-MM-DD'),
                  )}{' '}
                  {t('collapse.workingHours.label')}
                </span>
              )}
            </div>
          ),
          children: (
            <MonthPanelContent
              monthItem={monthItem}
              columnsVisibilityMap={columnsVisibilityMap}
              allColumns={allColumns}
              isExpanded={shouldExpand}
            />
          ),
        };
      }),
    [monthsInRange, t, activeKey, columnsVisibilityMap, allColumns, shouldExpand],
  );

  return (
    <Collapse
      items={items}
      destroyOnHidden
      accordion
      className={styles.collapse}
      bordered={false}
      activeKey={activeKey}
      onChange={(key) => setActiveKey(key)}
    />
  );
};

export default TimesheetCollapse;
